import { z } from "zod";

/**
 * Site IDs supported by Mercado Pago. The lib targets MLA (Argentina) primarily;
 * other LATAM sites may work for the read paths but the full Subscriptions flow
 * is only verified against MLA.
 */
export const SiteIdSchema = z.enum(["MLA", "MLB", "MLM", "MCO", "MLC", "MLU"]);
export type SiteId = z.infer<typeof SiteIdSchema>;

/**
 * Currency identifiers MP exposes. ARS is the supported case for v0.1.
 */
export const CurrencyIdSchema = z.enum(["ARS", "USD", "BRL", "MXN"]);
export type CurrencyId = z.infer<typeof CurrencyIdSchema>;

/**
 * Recurrence frequency unit for a subscription's auto_recurring config.
 */
export const FrequencyTypeSchema = z.enum(["months", "days"]);
export type FrequencyType = z.infer<typeof FrequencyTypeSchema>;

/**
 * Lifecycle states a Mercado Pago preapproval can be in. The string is the
 * canonical MP value; we widen to `string` for forward compatibility because
 * MP has historically introduced new states without notice.
 */
export const PreapprovalStatusSchema = z.union([
  z.literal("pending"),
  z.literal("authorized"),
  z.literal("paused"),
  z.literal("cancelled"),
  z.string(),
]);
export type PreapprovalStatus = z.infer<typeof PreapprovalStatusSchema>;

export const AutoRecurringSchema = z.object({
  frequency: z.number().int().positive(),
  frequency_type: FrequencyTypeSchema,
  transaction_amount: z.number().positive(),
  currency_id: CurrencyIdSchema,
  start_date: z.string().optional(),
  end_date: z.string().optional(),
});
export type AutoRecurring = z.infer<typeof AutoRecurringSchema>;

export const PreapprovalSchema = z.object({
  id: z.string(),
  status: PreapprovalStatusSchema,
  payer_email: z.string(),
  init_point: z.string().url(),
  external_reference: z.string().optional(),
  date_created: z.string(),
  last_modified: z.string(),
  next_payment_date: z.string().optional(),
  payer_id: z.union([z.string(), z.number()]).optional(),
  auto_recurring: AutoRecurringSchema,
});
export type Preapproval = z.infer<typeof PreapprovalSchema>;

/**
 * Input for creating a preapproval (subscription). Internal field names match
 * MP API semantics; the public client method maps from camelCase Naza-friendly
 * params to the snake_case payload MP expects.
 */
export interface CreatePreapprovalParams {
  /** Short customer-facing description shown at checkout. */
  reason: string;
  /** Email of the buyer. Cannot equal the seller account's email (MP rejects). */
  payerEmail: string;
  /** Recurring amount per cycle. */
  amount: number;
  /** ARS for Argentina. Other currencies depend on the seller account's site. */
  currency: CurrencyId;
  /** Recurrence frequency (e.g., 1 + months = monthly). */
  frequency: number;
  frequencyType: FrequencyType;
  /** HTTPS URL where MP redirects the buyer after first payment. localhost rejected. */
  backUrl: string;
  /** Optional client-side identifier for the subscription. */
  externalReference?: string;
  /**
   * Optional explicit idempotency key. If omitted, the client auto-generates
   * a UUID v4 per call. Pass a deterministic key (e.g., hash of inputs) when
   * you need cross-retry idempotency from the agent layer.
   */
  idempotencyKey?: string;
}

/**
 * The shape of an MP webhook notification body for `topic=preapproval`. MP's
 * webhook payload varies by event type; this is the union of fields seen in
 * production.
 */
export const WebhookBodySchema = z
  .object({
    type: z.string().optional(),
    topic: z.string().optional(),
    action: z.string().optional(),
    data: z.object({ id: z.union([z.string(), z.number()]) }).optional(),
    resource: z.string().optional(),
    user_id: z.union([z.string(), z.number()]).optional(),
    api_version: z.string().optional(),
    date_created: z.string().optional(),
    id: z.union([z.string(), z.number()]).optional(),
    live_mode: z.boolean().optional(),
  })
  .passthrough();
export type WebhookBody = z.infer<typeof WebhookBodySchema>;

/**
 * Normalized webhook event after parsing. The library extracts topic + dataId
 * from either query params or body, since MP sends them in either location
 * depending on integration version.
 */
export interface ParsedWebhookEvent {
  /** Topic of the event, e.g., "preapproval", "payment", "subscription_authorized_payment". */
  topic: string;
  /** ID of the affected resource. */
  dataId: string;
  /** Action descriptor when present (e.g., "updated", "created"). */
  action: string | null;
  /** Raw body MP sent, for caller inspection / debugging. */
  raw: WebhookBody;
}

// ─────────────────────────────────────────────────────────────────────────────
// Payments (v0.2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Top-level lifecycle status of a payment. MP-canonical values; widened to
 * string for forward compatibility.
 */
export const PaymentStatusSchema = z.union([
  z.literal("pending"),
  z.literal("approved"),
  z.literal("authorized"),
  z.literal("in_process"),
  z.literal("in_mediation"),
  z.literal("rejected"),
  z.literal("cancelled"),
  z.literal("refunded"),
  z.literal("charged_back"),
  z.string(),
]);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

/** Status detail — finer granularity inside a status (e.g., why rejected). */
export type PaymentStatusDetail = string;

/**
 * The full Payment object MP returns. Many fields are optional because they
 * vary by payment method, status, and integration mode (Checkout Pro vs API).
 */
export const PaymentSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  status: PaymentStatusSchema,
  status_detail: z.string().nullable().optional(),
  date_created: z.string().nullable().optional(),
  date_approved: z.string().nullable().optional(),
  date_last_updated: z.string().nullable().optional(),
  transaction_amount: z.number(),
  currency_id: z.string(),
  installments: z.number().int().nullable().optional(),
  payment_method_id: z.string().nullable().optional(),
  payment_type_id: z.string().nullable().optional(),
  external_reference: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  payer: z
    .object({
      id: z.union([z.string(), z.number()]).optional(),
      email: z.string().nullable().optional(),
      identification: z
        .object({
          type: z.string().nullable().optional(),
          number: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    })
    .passthrough()
    .optional(),
  transaction_details: z
    .object({
      net_received_amount: z.number().nullable().optional(),
      total_paid_amount: z.number().nullable().optional(),
      installment_amount: z.number().nullable().optional(),
    })
    .passthrough()
    .optional(),
}).passthrough();
export type Payment = z.infer<typeof PaymentSchema>;

/** Params for creating a payment (Checkout API / transparent flow). */
export interface CreatePaymentParams {
  /** Amount in account currency. ARS for Argentina. */
  transactionAmount: number;
  /** Number of installments. Use 1 for no cuotas; AR cards typically allow up to 12. */
  installments?: number;
  /** MP payment_method_id — `visa`, `master`, `naranja`, `account_money`, etc. */
  paymentMethodId: string;
  /** Payer email — REQUIRED. Cannot equal seller email. */
  payerEmail: string;
  /** Card token from MP frontend SDK (Cardform). Required for credit/debit; omit for `account_money` etc. */
  token?: string;
  /** Description shown in payer's MP statement. */
  description?: string;
  /** Your-system identifier for correlation. */
  externalReference?: string;
  /** Optional payer identification (DNI/CUIT) — required for some payment types. */
  identification?: { type: "DNI" | "CUIT" | "CUIL"; number: string };
  /** Webhook override URL. Falls back to dashboard config if omitted. */
  notificationUrl?: string;
  /**
   * Additional information passed to MP for fraud scoring + receipt details.
   *
   * **Critical for fraud scoring**: every field you fill in here improves
   * MP's risk model's confidence and reduces false-positive rejections.
   * For card payments above ~$50k ARS, ALWAYS include `payer` + `ip_address`
   * + `shipments` (when applicable) — payments without enrichment have a
   * 3-5x higher rejection rate per MP's published guidance (RG 5286/2023).
   */
  additionalInfo?: {
    /** Items in the cart — used for receipt + fraud scoring. */
    items?: Array<{
      id?: string;
      title: string;
      quantity: number;
      unit_price: number;
      description?: string;
      picture_url?: string;
      category_id?: string;
    }>;
    /**
     * Buyer profile fields. The more fields populated, the better MP's
     * risk engine can assess the transaction. `registration_date` is
     * particularly impactful (newer accounts = higher risk).
     */
    payer?: {
      first_name?: string;
      last_name?: string;
      phone?: {
        area_code?: string;
        number?: string;
      };
      address?: {
        zip_code?: string;
        street_name?: string;
        street_number?: number;
      };
      /** ISO 8601 date — when the buyer registered on YOUR platform. */
      registration_date?: string;
      /**
       * Whether the buyer is a returning customer. Strong fraud-scoring
       * signal — repeat buyers are lower risk.
       */
      authentication_type?: "Cellphone" | "Email" | "Facebook" | "Gmail" | "Native app" | string;
      /** Whether the buyer has set up 2FA on YOUR platform. */
      is_prime_user?: boolean;
      /** Whether the buyer has paid you successfully before. */
      is_first_purchase_online?: boolean;
      /** ISO 8601 — last successful purchase on your platform. */
      last_purchase?: string;
    };
    /**
     * Shipping info. Used by fraud engine to compare against payer address.
     * Mismatch (different city/province) = higher risk score.
     */
    shipments?: {
      receiver_address?: {
        zip_code?: string;
        street_name?: string;
        street_number?: number;
        floor?: string;
        apartment?: string;
        city_name?: string;
        state_name?: string;
        country_name?: string;
      };
      /** Express shipment flag — fraud engine treats high-value+express as suspicious. */
      express_shipment?: boolean;
      /** True if the buyer picks up at a store (no shipment). Reduces fraud risk. */
      local_pickup?: boolean;
    };
    /**
     * Buyer's IP address (from your X-Forwarded-For / req.headers).
     * **Strongly recommended** for any card payment — MP's fraud engine
     * checks IP geolocation, ASN reputation, proxy/VPN detection.
     * Without IP, MP defaults to "unknown" which raises risk score.
     */
    ip_address?: string;
    /** Referrer URL — useful for affiliate / channel attribution + fraud signal. */
    referral_url?: string;
  };
  /** Statement descriptor — what shows on the buyer's card statement. Max 13 chars. */
  statementDescriptor?: string;
  /** When true, capture is deferred (only for credit cards) — useful for hold flows. */
  capture?: boolean;
  /** Idempotency key — pass the same value on retries to dedupe. Required for non-GET. */
  idempotencyKey?: string;
}

export interface SearchPaymentsParams {
  /** Filter by external_reference (your-system id). */
  externalReference?: string;
  /** Filter by payment status. */
  status?: PaymentStatus;
  /** Filter by payer email. */
  payerEmail?: string;
  /** Date range for date_created (ISO 8601). */
  beginDate?: string;
  endDate?: string;
  /** Result page (default 0). */
  offset?: number;
  /** Page size (default 30, max 100). */
  limit?: number;
  /** Sort: e.g. "date_created" desc. */
  sort?: string;
  criteria?: "asc" | "desc";
}

export const PaymentsSearchResultSchema = z.object({
  paging: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
  results: z.array(PaymentSchema),
});
export type PaymentsSearchResult = z.infer<typeof PaymentsSearchResultSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Refunds
// ─────────────────────────────────────────────────────────────────────────────

export const RefundSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  payment_id: z.union([z.string(), z.number()]).transform(String),
  amount: z.number(),
  source: z
    .object({
      id: z.string().nullable().optional(),
      name: z.string().nullable().optional(),
      type: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  date_created: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
}).passthrough();
export type Refund = z.infer<typeof RefundSchema>;

export interface CreateRefundParams {
  paymentId: string;
  /** Partial refund amount. Omit for full refund. */
  amount?: number;
  /** Idempotency key — required for retry-safety. */
  idempotencyKey?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Checkout Pro (Preferences)
// ─────────────────────────────────────────────────────────────────────────────

export const PreferenceItemSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  description: z.string().optional(),
  picture_url: z.string().url().optional(),
  category_id: z.string().optional(),
  quantity: z.number().int().positive(),
  unit_price: z.number().positive(),
  currency_id: CurrencyIdSchema.optional(),
});
export type PreferenceItem = z.infer<typeof PreferenceItemSchema>;

export const PreferenceSchema = z.object({
  id: z.string(),
  init_point: z.string().url().optional(),
  sandbox_init_point: z.string().url().optional(),
  client_id: z.union([z.string(), z.number()]).optional(),
  collector_id: z.union([z.string(), z.number()]).optional(),
  items: z.array(PreferenceItemSchema).optional(),
  external_reference: z.string().nullable().optional(),
  date_created: z.string().nullable().optional(),
  expires: z.boolean().optional(),
  expiration_date_from: z.string().nullable().optional(),
  expiration_date_to: z.string().nullable().optional(),
}).passthrough();
export type Preference = z.infer<typeof PreferenceSchema>;

export interface CreatePreferenceParams {
  items: Array<{
    title: string;
    quantity: number;
    unit_price: number;
    currency_id?: CurrencyId;
    description?: string;
    picture_url?: string;
  }>;
  payer?: {
    name?: string;
    surname?: string;
    email?: string;
    phone?: { area_code?: string; number?: string };
    identification?: { type: string; number: string };
    address?: { street_name?: string; street_number?: number; zip_code?: string };
  };
  /** Where to send the buyer after success/failure/pending. */
  backUrls?: { success?: string; failure?: string; pending?: string };
  /** "approved" → auto-redirect on success; "all" → always; "" → never. */
  autoReturn?: "approved" | "all";
  /** Webhook URL. */
  notificationUrl?: string;
  /** Your-system id for correlation. */
  externalReference?: string;
  /** Max installments offered. Defaults to MP account config. */
  paymentMethods?: {
    excluded_payment_types?: Array<{ id: string }>;
    excluded_payment_methods?: Array<{ id: string }>;
    installments?: number;
    default_installments?: number;
  };
  /** Statement descriptor — shows on buyer's card statement. */
  statementDescriptor?: string;
  /** Expiration window for the link itself. */
  expires?: boolean;
  expirationDateFrom?: string;
  expirationDateTo?: string;
  /**
   * Marketplace split — if set, funds route to `collector_id` (the seller)
   * and `marketplaceFee` (in ARS) is credited to the marketplace's MP
   * account. v0.5+. See `MarketplaceParams` for details.
   */
  marketplace?: string;
  marketplaceFee?: number;
  /** Seller's MP user_id. Funds route here when set. */
  collectorId?: string | number;
  /**
   * Optional explicit idempotency key. If omitted, the client auto-generates
   * a UUID v4 per call. Pass a deterministic key (e.g., hash of items +
   * external_reference) when you need cross-retry idempotency from the
   * agent layer.
   */
  idempotencyKey?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Customers + Saved Cards
// ─────────────────────────────────────────────────────────────────────────────

export const CustomerSchema = z.object({
  id: z.string(),
  email: z.string(),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  phone: z
    .object({ area_code: z.string().nullable().optional(), number: z.string().nullable().optional() })
    .nullable()
    .optional(),
  identification: z
    .object({ type: z.string().nullable().optional(), number: z.string().nullable().optional() })
    .nullable()
    .optional(),
  date_created: z.string().nullable().optional(),
  date_last_updated: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
}).passthrough();
export type Customer = z.infer<typeof CustomerSchema>;

export const CustomerCardSchema = z.object({
  id: z.string(),
  customer_id: z.string(),
  expiration_month: z.number().int().nullable().optional(),
  expiration_year: z.number().int().nullable().optional(),
  first_six_digits: z.string().nullable().optional(),
  last_four_digits: z.string().nullable().optional(),
  payment_method: z
    .object({
      id: z.string().nullable().optional(),
      name: z.string().nullable().optional(),
      payment_type_id: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  date_created: z.string().nullable().optional(),
}).passthrough();
export type CustomerCard = z.infer<typeof CustomerCardSchema>;

export interface CreateCustomerParams {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: { areaCode?: string; number?: string };
  identification?: { type: "DNI" | "CUIT" | "CUIL"; number: string };
  description?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Payment Methods + Installments
// ─────────────────────────────────────────────────────────────────────────────

export const PaymentMethodSchema = z.object({
  id: z.string(),
  name: z.string(),
  payment_type_id: z.string(),
  status: z.string(),
  thumbnail: z.string().nullable().optional(),
  secure_thumbnail: z.string().nullable().optional(),
  min_allowed_amount: z.number().nullable().optional(),
  max_allowed_amount: z.number().nullable().optional(),
}).passthrough();
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const InstallmentOfferSchema = z.object({
  payment_method_id: z.string(),
  payment_type_id: z.string(),
  issuer: z
    .object({
      id: z.union([z.string(), z.number()]).optional(),
      name: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  payer_costs: z.array(
    z.object({
      installments: z.number().int(),
      installment_rate: z.number(),
      discount_rate: z.number().nullable().optional(),
      installment_amount: z.number(),
      total_amount: z.number(),
      recommended_message: z.string().nullable().optional(),
    }).passthrough(),
  ),
}).passthrough();
export type InstallmentOffer = z.infer<typeof InstallmentOfferSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Account
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// QR (in-store dynamic)
// ─────────────────────────────────────────────────────────────────────────────

export const QrOrderSchema = z.object({
  in_store_order_id: z.string(),
  qr_data: z.string(),
}).passthrough();
export type QrOrder = z.infer<typeof QrOrderSchema>;

export interface CreateQrPaymentParams {
  /** Pre-configured POS external_id from MP dashboard. Required. */
  externalPosId: string;
  /** Total amount in ARS. */
  totalAmount: number;
  /** Display title shown to the buyer when scanning. */
  title: string;
  description?: string;
  /** Webhook URL — MP fires `point_integration_wh` then `payment` topic. */
  notificationUrl?: string;
  /** Your-system identifier for correlation. */
  externalReference?: string;
  /** ISO 8601 expiration (default 10 min from now). */
  expirationDate?: string;
  /** Itemized line items (optional but improves analytics). */
  items?: Array<{
    title: string;
    quantity: number;
    unit_price: number;
    unit_measure?: string;
    total_amount?: number;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Card tokens (for charge_saved_card)
// ─────────────────────────────────────────────────────────────────────────────

export const CardTokenSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  date_due: z.string().optional(),
  card_id: z.string().optional(),
  cardholder: z.unknown().optional(),
}).passthrough();
export type CardToken = z.infer<typeof CardTokenSchema>;

export interface CreateCardTokenParams {
  /** Saved card id (from list_customer_cards). */
  cardId: string;
  /** Customer that owns the card. */
  customerId: string;
  /** CVV — required for AR; MP doesn't store CVV. */
  securityCode: string;
}

export const AccountInfoSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  email: z.string().nullable().optional(),
  nickname: z.string().nullable().optional(),
  country_id: z.string().nullable().optional(),
  site_id: z.string().nullable().optional(),
  user_type: z.string().nullable().optional(),
  status: z
    .object({ user_type: z.string().nullable().optional() })
    .passthrough()
    .nullable()
    .optional(),
}).passthrough();
export type AccountInfo = z.infer<typeof AccountInfoSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Subscription Plans (preapproval_plan — reusable plan definitions)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A reusable subscription plan. Different from a per-customer subscription:
 * a plan defines the price + frequency once, then customers subscribe to it
 * via `subscribe_to_plan` (which creates a preapproval pointing at the plan).
 *
 * Use plans for SaaS-style billing where you have a fixed set of tiers
 * (Básico/Pro/Enterprise) instead of negotiating amounts per customer.
 */
export const SubscriptionPlanSchema = z.object({
  id: z.string(),
  status: z.string(),
  reason: z.string(),
  back_url: z.string().url().optional(),
  external_reference: z.string().nullable().optional(),
  date_created: z.string(),
  last_modified: z.string(),
  auto_recurring: AutoRecurringSchema,
}).passthrough();
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

export interface CreateSubscriptionPlanParams {
  /** Customer-facing plan name shown at checkout. */
  reason: string;
  /** Where MP redirects buyer after first payment. HTTPS only. */
  backUrl: string;
  /** Recurrence (e.g., 1 + months = monthly). */
  frequency: number;
  frequencyType: FrequencyType;
  /** Amount per cycle. */
  amount: number;
  /** ARS for AR. */
  currency: CurrencyId;
  /** Optional plan-level identifier from your system. */
  externalReference?: string;
  /** Free trial days before first charge. */
  freeTrialFrequency?: number;
  freeTrialFrequencyType?: FrequencyType;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stores + POS (for QR payments setup)
// ─────────────────────────────────────────────────────────────────────────────

export const StoreSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  name: z.string().optional(),
  external_id: z.string().optional(),
  date_creation: z.string().optional(),
  location: z
    .object({
      address_line: z.string().optional(),
      city_name: z.string().optional(),
      state_name: z.string().optional(),
      country_id: z.string().optional(),
      latitude: z.number().optional(),
      longitude: z.number().optional(),
    })
    .passthrough()
    .optional(),
}).passthrough();
export type Store = z.infer<typeof StoreSchema>;

export interface CreateStoreParams {
  /** Display name for the store. */
  name: string;
  /** Caller-defined identifier (must be unique within the seller's stores). */
  externalId: string;
  /** Optional physical location. */
  location?: {
    addressLine?: string;
    cityName?: string;
    stateName?: string;
    countryId?: string;
    latitude?: number;
    longitude?: number;
  };
}

export const PosSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  name: z.string().optional(),
  external_id: z.string().optional(),
  store_id: z.union([z.string(), z.number()]).optional(),
  category: z.number().int().optional(),
  fixed_amount: z.boolean().optional(),
  qr: z
    .object({
      template_image: z.string().optional(),
      image: z.string().optional(),
    })
    .passthrough()
    .optional(),
  date_creation: z.string().optional(),
}).passthrough();
export type Pos = z.infer<typeof PosSchema>;

export interface CreatePosParams {
  /** Display name. */
  name: string;
  /** Caller-defined POS id (used in QR endpoints; unique within store). */
  externalId: string;
  /** Parent store id (number from createStore). */
  storeId: string | number;
  /** MP category code (default 621102 = Other Food and Beverage Services). */
  category?: number;
  /** If true, the QR has a fixed amount; if false, dynamic per-order. */
  fixedAmount?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Disputes / Chargebacks (read-only)
// ─────────────────────────────────────────────────────────────────────────────

export const DisputeSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  status: z.string(),
  resource: z.string().optional(),
  resource_id: z.union([z.string(), z.number()]).optional(),
  amount: z.number().optional(),
  date_created: z.string().optional(),
  reason: z.string().optional(),
  resolution: z
    .object({
      reason: z.string().optional(),
      result: z.string().optional(),
      date: z.string().optional(),
    })
    .passthrough()
    .optional(),
  /** Documents the buyer / seller submitted as evidence. */
  documents: z.array(z.unknown()).optional(),
  /** Buyer's stated complaint. */
  reason_description: z.string().optional(),
}).passthrough();
export type Dispute = z.infer<typeof DisputeSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Subscription Payment History (authorized_payments under a preapproval)
// ─────────────────────────────────────────────────────────────────────────────

export const SubscriptionPaymentSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  preapproval_id: z.string().optional(),
  status: z.string(),
  payment_id: z.union([z.string(), z.number()]).nullable().optional(),
  transaction_amount: z.number().optional(),
  currency_id: z.string().optional(),
  date_created: z.string().optional(),
  debit_date: z.string().optional(),
  next_retry_date: z.string().nullable().optional(),
  retry_attempt: z.number().optional(),
  reason: z.string().optional(),
}).passthrough();
export type SubscriptionPayment = z.infer<typeof SubscriptionPaymentSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Identification Types + Issuers (lookup helpers)
// ─────────────────────────────────────────────────────────────────────────────

export const IdentificationTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  min_length: z.number().optional(),
  max_length: z.number().optional(),
}).passthrough();
export type IdentificationType = z.infer<typeof IdentificationTypeSchema>;

export const IssuerSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  name: z.string(),
  secure_thumbnail: z.string().nullable().optional(),
  thumbnail: z.string().nullable().optional(),
  processing_mode: z.string().optional(),
  status: z.string().optional(),
}).passthrough();
export type Issuer = z.infer<typeof IssuerSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Webhooks (configure subscriptions to topics)
// ─────────────────────────────────────────────────────────────────────────────

/** Topics MP can fire webhooks for. Add more as MP exposes them. */
export const WebhookTopicSchema = z.enum([
  "payment",
  "subscription_authorized_payment",
  "subscription_preapproval",
  "merchant_order",
  "point_integration_wh",
  "stop_delivery_op_wh",
]);
export type WebhookTopic = z.infer<typeof WebhookTopicSchema>;

export const WebhookConfigSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  url: z.string().url().optional(),
  status: z.string().optional(),
  topic: z.string().optional(),
  date_created: z.string().optional(),
  date_modified: z.string().optional(),
}).passthrough();
export type WebhookConfig = z.infer<typeof WebhookConfigSchema>;

export interface CreateWebhookParams {
  url: string;
  /** Topic to subscribe to. */
  topic: WebhookTopic | string;
}

// ─────────────────────────────────────────────────────────────────────────────
// v0.5 — OAuth Marketplace flow (link third-party MP accounts)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Token response from MP's OAuth `/oauth/token` endpoint. The `access_token`
 * is what you use to make API calls AS the linked seller; the `refresh_token`
 * is what you use to refresh the access_token before expiration (~6 hours).
 *
 * **Persist the refresh_token**: it does NOT expire and is the only way to
 * keep the integration alive long-term.
 *
 * **Always store `user_id`** alongside the tokens — it identifies WHICH
 * seller these tokens belong to (you'll have many in a marketplace).
 */
export const OAuthTokenSchema = z.object({
  access_token: z.string(),
  token_type: z.string().optional(),
  /** Seconds until access_token expires. Typically 21600 (6h). */
  expires_in: z.number().optional(),
  scope: z.string().optional(),
  /** The MP user_id of the seller who authorized your app. */
  user_id: z.union([z.string(), z.number()]).transform(String),
  refresh_token: z.string().optional(),
  public_key: z.string().optional(),
  live_mode: z.boolean().optional(),
}).passthrough();
export type OAuthToken = z.infer<typeof OAuthTokenSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// v0.5 — Order Management API (the new API replacing some Preference flows)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Status of an Order. Distinct from Payment status — an Order can have
 * multiple payments and the Order status reflects the aggregate state.
 */
export const OrderStatusSchema = z.union([
  z.literal("created"),
  z.literal("processed"),
  z.literal("action_required"),
  z.literal("canceled"),
  z.literal("expired"),
  z.literal("refunded"),
  z.string(),
]);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const OrderItemSchema = z.object({
  title: z.string(),
  unit_price: z.number(),
  quantity: z.number(),
  description: z.string().optional(),
  id: z.string().optional(),
  category_id: z.string().optional(),
  picture_url: z.string().optional(),
}).passthrough();
export type OrderItem = z.infer<typeof OrderItemSchema>;

export const OrderSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  type: z.string().optional(),
  status: OrderStatusSchema.optional(),
  status_detail: z.string().optional(),
  external_reference: z.string().optional(),
  total_amount: z.union([z.number(), z.string()]).optional(),
  /** Currency for this Order (e.g. "ARS"). */
  currency_id: z.string().optional(),
  date_created: z.string().optional(),
  date_last_updated: z.string().optional(),
  items: z.array(OrderItemSchema).optional(),
  /** Underlying transactions (payments) attached to this Order. */
  transactions: z
    .object({
      payments: z.array(z.unknown()).optional(),
      refunds: z.array(z.unknown()).optional(),
    })
    .passthrough()
    .optional(),
  /** Capture mode: "automatic" (charges immediately) or "manual" (auth-only). */
  capture_mode: z.string().optional(),
}).passthrough();
export type Order = z.infer<typeof OrderSchema>;

export interface CreateOrderParams {
  /**
   * Order type. Common values:
   * - "online" — checkout-style (the most common)
   * - "in_store" — POS QR / in-person
   */
  type: "online" | "in_store" | string;
  /** Currency (e.g. "ARS"). */
  currency_id?: string;
  /** External reference for reconciliation (your internal id). */
  external_reference?: string;
  /** Items being ordered. */
  items?: OrderItem[];
  /** Total amount if you'd rather not itemize. */
  total_amount?: number;
  /** Customer info (payer). */
  payer?: {
    email?: string;
    first_name?: string;
    last_name?: string;
    identification?: { type?: string; number?: string };
  };
  /**
   * Capture mode:
   * - "automatic" (default) — charge immediately when paid.
   * - "manual" — authorize only, capture later via captureOrder().
   */
  capture_mode?: "automatic" | "manual";
  /**
   * Notification URL — MP fires webhooks for order lifecycle events here.
   */
  notification_url?: string;
  /**
   * Marketplace fee + collector — required for marketplace integrations
   * where YOU collect on behalf of a third-party seller (you take a fee,
   * the rest goes to the seller).
   */
  marketplace?: string;
  marketplace_fee?: number;
  collector_id?: string | number;
}

// ─────────────────────────────────────────────────────────────────────────────
// v0.5 — Marketplace split payments (also usable on regular Preference)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Marketplace fee + collector params, applied to a Preference or Order.
 *
 * **How it works**: when you create a Preference or Order with
 * `collector_id` set to a SELLER's MP user_id (not yours), and you have a
 * valid OAuth access_token for that seller, MP routes the funds to the
 * seller's MP account, and credits `marketplace_fee` (in ARS, not %) to
 * YOUR marketplace account.
 *
 * Used for two-sided platforms (Rappi, MercadoLibre, Tienda Nube, etc.)
 * where your platform takes a fee and the seller keeps the rest.
 */
export interface MarketplaceParams {
  /**
   * Marketplace identifier — your application's marketplace name (you set
   * this when registering your app in MP's dev panel).
   */
  marketplace?: string;
  /**
   * Fee in ARS (NOT a percentage) that goes to your marketplace account.
   * Compute it from your own commission rate before passing.
   */
  marketplace_fee?: number;
  /**
   * The SELLER's MP user_id. The funds go to this account; the
   * `marketplace_fee` is split off and goes to YOUR account.
   * Get this from `OAuthToken.user_id` after the seller authorizes your app.
   */
  collector_id?: string | number;
  /**
   * Optional: split among multiple sellers. If set, `collector_id` /
   * `marketplace_fee` are ignored.
   */
  application_fee?: number;
}


// ─────────────────────────────────────────────────────────────────────────────
// v0.6 — Account Balance + Movements
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Account balance snapshot. Sum of `available + unavailable` is the seller's
 * total Mercado Pago balance.
 *
 * - `available` — funds the seller can withdraw or pay with right now.
 * - `unavailable` — funds in retention (typically 14-21 days for new sellers
 *   or for risk-flagged transactions). Becomes `available` automatically.
 */
export const AccountBalanceSchema = z.object({
  user_id: z.union([z.string(), z.number()]).transform(String).optional(),
  available_balance: z.number(),
  unavailable_balance: z.number(),
  total_amount: z.number(),
  currency_id: z.string().default("ARS"),
}).passthrough();
export type AccountBalance = z.infer<typeof AccountBalanceSchema>;

/**
 * Account movement (a single line in the seller's MP "movements" log).
 * Includes incoming payments, outgoing transfers, refunds, holdings, etc.
 */
export const AccountMovementSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  type: z.string(),
  description: z.string().optional(),
  amount: z.number(),
  currency_id: z.string().optional(),
  status: z.string().optional(),
  date_created: z.string().optional(),
  date_released: z.string().optional(),
  reference_id: z.union([z.string(), z.number()]).optional(),
  payment_id: z.union([z.string(), z.number()]).optional(),
}).passthrough();
export type AccountMovement = z.infer<typeof AccountMovementSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// v0.6 — Settlements (release_money)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A scheduled or completed transfer from the MP account to the seller's
 * registered bank account (CBU). Settlements are the core of "when do I
 * actually get paid".
 */
export const SettlementSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  status: z.string().optional(),
  amount: z.number().optional(),
  currency_id: z.string().optional(),
  date_created: z.string().optional(),
  date_scheduled: z.string().optional(),
  date_processed: z.string().optional(),
  bank_account: z
    .object({
      cbu: z.string().optional(),
      bank_name: z.string().optional(),
    })
    .passthrough()
    .optional(),
}).passthrough();
export type Settlement = z.infer<typeof SettlementSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// v0.6 — 3DS (Strong Customer Authentication) info derived from a Payment
// ─────────────────────────────────────────────────────────────────────────────

export type ThreeDSStatus =
  | "not_required"   // No 3DS challenge issued (low-risk transaction).
  | "frictionless"   // Issuer approved without challenge.
  | "challenge_required" // Buyer must complete the issuer's challenge.
  | "rejected"       // Failed authentication.
  | "unknown";       // Couldn't determine — surface to ops.

export interface ThreeDSInfo {
  /** High-level status — the field most agents care about. */
  status: ThreeDSStatus;
  /** Raw `three_d_secure_mode` field from the payment, if present. */
  mode: string | null;
  /**
   * URL the buyer must visit to complete the 3DS challenge, if one was
   * triggered. `null` for frictionless/not_required.
   */
  challengeUrl: string | null;
  /** Human-readable explanation suitable for surfacing to the user. */
  description: string;
}


// ─────────────────────────────────────────────────────────────────────────────
// v0.7 — Merchant Orders
//
// MerchantOrder is the parent entity for Payments associated with a single
// Preference. Each Preference can collect multiple Payments (partial
// installments, retries) — the MerchantOrder aggregates them and reports
// the final paid_amount, shipping status, etc. Useful for reconciling
// "which payments belong to which order" at the dashboard level.
// ─────────────────────────────────────────────────────────────────────────────

export const MerchantOrderSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  status: z.string().optional(),
  external_reference: z.string().nullable().optional(),
  preference_id: z.union([z.string(), z.number()]).optional(),
  payments: z.array(z.unknown()).optional(),
  shipments: z.array(z.unknown()).optional(),
  payer: z.unknown().optional(),
  collector: z.unknown().optional(),
  marketplace: z.string().optional(),
  total_amount: z.number().optional(),
  paid_amount: z.number().optional(),
  refunded_amount: z.number().optional(),
  shipping_cost: z.number().optional(),
  date_created: z.string().optional(),
  last_updated: z.string().optional(),
  site_id: z.string().optional(),
  order_status: z.string().optional(),
}).passthrough();
export type MerchantOrder = z.infer<typeof MerchantOrderSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// v0.7 — Bank Accounts (the CBUs the seller has registered for payouts)
// ─────────────────────────────────────────────────────────────────────────────

export const BankAccountSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  cbu: z.string().optional(),
  alias: z.string().nullable().optional(),
  bank_name: z.string().optional(),
  account_type: z.string().optional(),
  status: z.string().optional(),
  is_default: z.boolean().optional(),
  date_created: z.string().optional(),
}).passthrough();
export type BankAccount = z.infer<typeof BankAccountSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// v0.7 — Point Devices (physical POS hardware: Point Smart, Point Tap to Pay)
//
// Distinct from the "Pos" (logical sale point) entity. PointDevices are the
// physical terminals you use at a brick-and-mortar shop. Use this API to:
// - List the devices linked to your account
// - Switch a device between PDV mode (linked to Pos) and STANDALONE mode
// - Push a payment intent directly to a device (the device prompts the
//   buyer to tap/insert; no QR involved)
// ─────────────────────────────────────────────────────────────────────────────

export const PointDeviceSchema = z.object({
  id: z.string(),
  pos_id: z.union([z.string(), z.number()]).nullable().optional(),
  store_id: z.union([z.string(), z.number()]).nullable().optional(),
  external_pos_id: z.string().nullable().optional(),
  operating_mode: z.union([z.literal("PDV"), z.literal("STANDALONE"), z.string()]).optional(),
}).passthrough();
export type PointDevice = z.infer<typeof PointDeviceSchema>;

export type PointPaymentIntentState =
  | "OPEN"        // Sent to device, awaiting buyer interaction
  | "PROCESSING"  // Buyer tapped/inserted, processing
  | "FINISHED"    // Done (approved or rejected)
  | "CANCELED"
  | "ERROR"
  | string;

export const PointPaymentIntentSchema = z.object({
  id: z.string(),
  device_id: z.string().optional(),
  amount: z.number().optional(),
  state: z.union([
    z.literal("OPEN"),
    z.literal("PROCESSING"),
    z.literal("FINISHED"),
    z.literal("CANCELED"),
    z.literal("ERROR"),
    z.string(),
  ]).optional(),
  payment: z
    .object({
      id: z.union([z.string(), z.number()]).optional(),
      type: z.string().optional(),
      installments: z.number().optional(),
      installments_cost: z.string().optional(),
      print_on_terminal: z.boolean().optional(),
    })
    .passthrough()
    .optional(),
  additional_info: z.unknown().optional(),
}).passthrough();
export type PointPaymentIntent = z.infer<typeof PointPaymentIntentSchema>;

export interface CreatePointPaymentIntentParams {
  /** Amount in centavos (yes, centavos — Point API differs from Payments API). */
  amount: number;
  /** Description shown on the device screen + the buyer's receipt. */
  description?: string;
  /** Your-system reference (echoed back in the resulting Payment). */
  externalReference?: string;
  /** Number of installments (default 1). */
  installments?: number;
  /** "seller" or "buyer" — who pays the installment cost. */
  installmentsCost?: "seller" | "buyer";
  /**
   * Print receipt on the terminal (default true). Some kiosks set false.
   */
  printOnTerminal?: boolean;
  /** Ticket number (your sequential receipt number). */
  ticketNumber?: string;
}

